import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import GIButton from '@components/common/button/GIButton';
import { configs } from '@constants/configs/configs';
import AlertInfo from '@components/common/alert/AlertInfo';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { BREAK_POINTS } from '@constants/common/breakpoints';
import { cleanQueryParams } from '@utils/miscellaneous/urlSearchParams';

const StyledFinishContainer = styled.div`
    .gi-alert__info {
        color: ${({ theme }) => theme.primaryDark};
        border-left: ${({ theme }) => `3px solid ${theme.primaryMain}`};
        padding: 16px;
        margin-bottom: 20px !important;
    }
`;
const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: ${BREAK_POINTS.sm}) {
        flex-direction: column;
    }

    .gi-button {
        background-color: ${({ theme }) => theme.primaryDark};
        margin: 30px 20px 30px;
        text-transform: none;
        @media (max-width: ${BREAK_POINTS.sm}) {
            margin: 10px 20px 10px !important;
        }
    }
`;

const StyledContinueButton = styled.div`
    display: flex;
    justify-content: flex-end;
    .gi-button {
        background-color: ${({ theme }) => theme.primaryMain};
        margin: 30px 20px 30px;
        text-transform: none;
    }
`;

const StyledNote = styled.div`
    padding: 20px 0px;
`;

const TechincalErrorPage = ({ backToApplication }) => {
    const {
        routes: { RIDP_UPLOAD_DOCUMENT },
    } = configs;

    const navigate = useNavigate();
    const location = useLocation();

    const proceedToManualVerification = () => {
        const existingParams = new URLSearchParams(location.search);
        navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`);
    };
    return (
        <main id="main-content">
            <StyledFinishContainer>
                <h1 className="usa-sr-only">
                    <FormattedMessage id="ridp.sidebar.finish" />
                    &nbsp;:&nbsp;
                    <FormattedMessage id="ridp.sidebar.header" />
                </h1>
                <FormattedMessage id="ridp.finish.technicalerror.para1" />
                <StyledButtonContainer>
                    <GIButton
                        type="button"
                        id="backToApplication"
                        labelId="ridp.finish.button1.label"
                        onClickHandler={backToApplication}
                    />
                    <FormattedMessage id="OR" />
                    <GIButton
                        type="button"
                        id="ridpManualVerification"
                        labelId="ridp.finish.button2.label"
                        onClickHandler={proceedToManualVerification}
                    />
                </StyledButtonContainer>
                <StyledNote>
                    <FormattedMessage
                        id="ridp.finish.technicalerror.para2"
                        values={{
                            br: <br />,
                            strong: chunks => <strong>{chunks}</strong>,
                        }}
                    />
                </StyledNote>
            </StyledFinishContainer>
        </main>
    );
};

const FinishTabContent = ({ backToApplication }) => {
    return (
        <main id="main-content">
            <StyledFinishContainer>
                <h1 className="usa-sr-only">
                    <FormattedMessage id="ridp.sidebar.finish" />
                    &nbsp;:&nbsp;
                    <FormattedMessage id="ridp.sidebar.header" />
                </h1>
                <AlertInfo id="ridp.finish.alert.header" />
                <FormattedMessage id="ridp.finish.para" />
                <StyledContinueButton>
                    <GIButton
                        type="button"
                        id="finish"
                        labelId="ridp.contactinfo.input.label.continue"
                        onClickHandler={backToApplication}
                    />
                </StyledContinueButton>
            </StyledFinishContainer>
        </main>
    );
};

const Finish = () => {
    const {
        routes: { RIDP_TECHNICAL_ERROR, SSAP, RIDP_VERIFICATION_COMPLETE },
    } = configs;
    const location = useLocation();
    const backToApplication = () => {
        const queryParams = new URLSearchParams(window.location.search);
        const existingParams = cleanQueryParams(queryParams);
        const coverageYear = existingParams['coverageYear'];
        const applicationType = existingParams['applicationType'];
        const caseNumber = existingParams['caseNumber'];
        if (caseNumber) {
            window.location.href = `${SSAP}?caseNumber=${caseNumber}`;
        } else if (coverageYear && applicationType) {
            window.location.href = `${SSAP}?applicationType=${applicationType}&coverageYear=${coverageYear}`;
        } else {
            window.location.href = `${SSAP}`;
        }
    };
    return (
        <>
            <ConditionalRendering isTrue={location.pathname === RIDP_TECHNICAL_ERROR}>
                <TechincalErrorPage backToApplication={backToApplication} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={location.pathname === RIDP_VERIFICATION_COMPLETE}>
                <FinishTabContent backToApplication={backToApplication} />
            </ConditionalRendering>
        </>
    );
};

export default Finish;
